import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'

import { BOOK_SHOWN } from '../../lib/bookAsk'
import { bookAskOf, bookFilterFrom, bookSearchFrom, emptyBookSays, searchFindsInactive } from '../../lib/bookFilter'
import { withActiveFirst } from '../../lib/inactive'
import { apiErrorMessage } from '../../lib/errors'
import { useBookFilter } from '../../lib/useBookFilter'
import type { TableColumn } from '../../lib/tableView'
import { useAddressTableView } from '../../lib/useAddressTableView'
import { listBrokers, type BrokerSummary } from './api'
import { ActivationBadge } from '../../components/ActivationPanel'
import { BookFilterBar } from '../../components/BookFilterBar'
import { FilterCell, FilterSummary, SortableHeader } from '../../components/TableHeads'
import { TableScroll } from '../../components/TableScroll'
import { historyRow } from '../../components/historyRow'
import { ComplianceBadge } from './ComplianceBadge'
import { complianceRank } from './compliance'
import { formatMcNumber } from './format'

/**
 * What the book's columns sort and filter by (TRKC-110). The name, the MC number, the paperwork,
 * the working relationship and the state are filtered from the bar above — the server's filters,
 * which reach past the first 500 listed — so their headings sort and their boxes stay empty;
 * the city is filtered here.
 */
/**
 * Inactive brokers — listed when the filter asks for them, or when a search finds them — stay
 * after the active ones whatever the column, as history does in every table (TRKC-110).
 */
const activeFirst = (broker: BrokerSummary) => (broker.active ? 0 : 1)

const BROKER_COLUMNS: readonly TableColumn<BrokerSummary>[] = [
  { key: 'name', sortValue: (broker) => broker.name },
  { key: 'mc', sortValue: (broker) => broker.mcNumber },
  {
    key: 'location',
    sortValue: (broker) => `${broker.city}, ${broker.state}`,
    filter: { kind: 'text', text: (broker) => `${broker.city}, ${broker.state}` },
  },
  { key: 'compliance', sortValue: (broker) => complianceRank(broker.complianceState) },
]

/**
 * The broker book: everyone the operation moves freight for, searchable by name or MC and
 * narrowed by the paperwork's standing, the working relationship and the state (TRKC-97) —
 * kept in the address, so going back to the book from a broker finds it as it was left.
 */
export function BrokersPage() {
  const { t } = useTranslation()
  const controls = useBookFilter()
  const { filter } = controls

  const [brokers, setBrokers] = useState<BrokerSummary[] | null>(null)
  // Stored unresolved and translated at render, so a language switch re-translates it.
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const asked = bookSearchFrom(filter).toString()
  const listed = useMemo(() => withActiveFirst((brokers ?? []).slice(0, BOOK_SHOWN)), [brokers])
  const view = useAddressTableView(listed, BROKER_COLUMNS, activeFirst)

  // Keyed on what the filter says, never on `t`: switching EN/ES asks nothing again. Every
  // filter asks the server, and answers can land out of order, so only the latest request
  // may write — a slower answer to filters the bar has left never replaces the book.
  useEffect(() => {
    let active = true
    setIsLoading(true)
    setLoadFailure(null)

    // One past what the page lists, so it can say when the search should narrow the book.
    listBrokers({ ...bookAskOf(bookFilterFrom(new URLSearchParams(asked))), limit: BOOK_SHOWN + 1 })
      .then((found) => {
        if (active) {
          setBrokers(found)
        }
      })
      .catch((caught: unknown) => {
        if (active) {
          setBrokers(null)
          setLoadFailure({ error: caught })
        }
      })
      .finally(() => {
        if (active) {
          setIsLoading(false)
        }
      })

    return () => {
      active = false
    }
  }, [asked])

  return (
    <section className="page">
      <header className="page__header page__header--row">
        <div>
          <h1>{t('brokers.title')}</h1>
          <p className="page__subtitle">{t('brokers.subtitle')}</p>
        </div>
        <Link className="button" to="/brokers/new">
          {t('brokers.register')}
        </Link>
      </header>

      <BookFilterBar book="brokers" controls={controls} isLoading={isLoading} />

      {loadFailure !== null && (
        <p className="alert" role="alert">
          {apiErrorMessage(loadFailure.error, t, 'brokers.loadError')}
        </p>
      )}

      {brokers !== null && brokers.length === 0 && (
        <p className="placeholder">
          {t(`brokers.${emptyBookSays(filter)}`)}
        </p>
      )}

      {/* A search at the default relationship finds the inactive ones too, and says so. */}
      {brokers !== null && searchFindsInactive(filter) && brokers.some((broker) => !broker.active) && (
        <p className="muted">{t('books.searchFindsInactive')}</p>
      )}

      {brokers !== null && brokers.length > BOOK_SHOWN && (
        <p className="muted" role="status">
          {t('brokers.firstOnly', { shown: BOOK_SHOWN })}
        </p>
      )}

      {brokers !== null && brokers.length > 0 && (
        <TableScroll label={t('brokers.title')}>
          <table className="table">
            <caption className="visually-hidden">{t('brokers.title')}</caption>
            <thead>
              <tr>
                <SortableHeader view={view} column="name">{t('brokers.name')}</SortableHeader>
                <SortableHeader view={view} column="mc">{t('brokers.mcNumber')}</SortableHeader>
                <SortableHeader view={view} column="location">{t('brokers.location')}</SortableHeader>
                <SortableHeader view={view} column="compliance">{t('brokers.compliance')}</SortableHeader>
              </tr>
              <tr className="table__filters">
                {BROKER_COLUMNS.map((column) => (
                  <FilterCell key={column.key} view={view} column={column} label={t(BROKER_HEADINGS[column.key] ?? column.key)} />
                ))}
              </tr>
            </thead>
            <tbody>
              {view.rows.length === 0 && (
                <tr>
                  <td colSpan={BROKER_COLUMNS.length} className="placeholder">
                    {t('brokers.noMatches')}
                  </td>
                </tr>
              )}
              {view.rows.map((broker) => (
                <tr key={broker.id} className={historyRow(!broker.active)}>
                  <td>
                    <Link to={`/brokers/${broker.id}`}>{broker.name}</Link>
                  </td>
                  <td className="table__numeric">{formatMcNumber(broker.mcNumber) ?? '—'}</td>
                  <td>{`${broker.city}, ${broker.state}`}</td>
                  <td>
                    <ComplianceBadge state={broker.complianceState} />{!broker.active && ' '}
                    <ActivationBadge active={broker.active} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
      {brokers !== null && brokers.length > 0 && <FilterSummary view={view} />}
    </section>
  )
}

/** The heading each column's filter box is named after. */
const BROKER_HEADINGS: Record<string, string> = {
  name: 'brokers.name',
  mc: 'brokers.mcNumber',
  location: 'brokers.location',
  compliance: 'brokers.compliance',
}
